// Pure aggregation helpers for the Messaging Reports page. The API returns
// counts grouped by day / hour / weekday x channel x member x direction x
// automated (see rhinoapi GET /analytics/messages/breakdown); everything on
// the page is derived from those rows here so filters apply instantly.
import moment from 'moment-timezone';
import {
  TYPE_SMS, TYPE_TWILIO, TYPE_SECURE, TYPE_RHINOGRAM, TYPE_FACEBOOK, TYPE_INSTAGRAM,
} from '../constants/Types';

export const MESSAGE_KINDS = [
  { id: 'all', label: 'All Messages' },
  { id: 'inbound', label: 'Inbound' },
  { id: 'outbound', label: 'Outbound (Manual)' },
  { id: 'automated', label: 'Outbound (Automated)' },
];

export const KIND_LABELS = { inbound: 'Inbound', outbound: 'Outbound', automated: 'Automated' };

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // MySQL WEEKDAY(): 0 = Monday

const CHANNEL_TYPE_LABELS = {
  [TYPE_SMS]: 'SMS',
  [TYPE_TWILIO]: 'SMS',
  [TYPE_SECURE]: 'Secure',
  [TYPE_RHINOGRAM]: 'Rhinogram',
  [TYPE_FACEBOOK]: 'Facebook',
  [TYPE_INSTAGRAM]: 'Instagram',
};

export const channelTypeLabel = (typeId) => CHANNEL_TYPE_LABELS[typeId] || 'Other';

export function rowKind(row) {
  if (row.incoming) return 'inbound';
  return row.automated ? 'automated' : 'outbound';
}

export function filterRows(rows, { memberId = null, channelId = null, kind = 'all' } = {}) {
  return rows.filter((row) => {
    if (memberId !== null && row.memberId !== memberId) return false;
    if (channelId !== null && row.channelId !== channelId) return false;
    if (kind !== 'all' && rowKind(row) !== kind) return false;
    return true;
  });
}

export const emptyTotals = () => ({
  total: 0, inbound: 0, outbound: 0, automated: 0, responseTimeSum: 0, responseTimeCount: 0,
});

export function addRow(totals, row) {
  const t = totals;
  t.total += row.count;
  t[rowKind(row)] += row.count;
  t.responseTimeSum += row.responseTimeSum || 0;
  t.responseTimeCount += row.responseTimeCount || 0;
  return t;
}

export function sumRows(rows) {
  return rows.reduce(addRow, emptyTotals());
}

// Events.responseTime is seconds; the page shows minutes with one decimal.
export function avgResponseMinutes(sum, count) {
  if (!count) return null;
  return Math.round((sum / count / 60) * 10) / 10;
}

export const formatMinutes = (minutes) => (minutes === null || minutes === undefined ? '-' : `${minutes} min`);

// Every calendar day in the picked range, in the report timezone.
export function daysInRange(start, end) {
  const days = [];
  const cursor = start.clone().startOf('day');
  const last = end.clone().startOf('day');
  while (!cursor.isAfter(last)) {
    days.push(cursor.format('YYYY-MM-DD'));
    cursor.add(1, 'day');
  }
  return days;
}

export const shortDay = (day) => moment(day, 'YYYY-MM-DD').format('MM/DD');

export function periodKey(day, grouping) {
  const m = moment(day, 'YYYY-MM-DD');
  if (grouping === 'week') return `Week of ${m.clone().startOf('isoWeek').format('MM/DD')}`;
  if (grouping === 'month') return m.format('MMM YYYY');
  if (grouping === 'day') return shortDay(day);
  return 'Total';
}

export function daysPerPeriod(days, grouping) {
  const counts = {};
  days.forEach((day) => {
    const key = periodKey(day, grouping);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

const finishTotals = (totals, dayCount) => ({
  ...totals,
  avgPerDay: dayCount ? Math.round((totals.total / dayCount) * 10) / 10 : 0,
  avgResponseMinutes: avgResponseMinutes(totals.responseTimeSum, totals.responseTimeCount),
});

// Summary table: one row per period (or a single Total row).
export function summaryByPeriod(rows, days, grouping) {
  const perPeriodDays = daysPerPeriod(days, grouping);
  const buckets = {};
  Object.keys(perPeriodDays).forEach((key) => { buckets[key] = emptyTotals(); });
  rows.forEach((row) => {
    const key = periodKey(row.day, grouping);
    if (!buckets[key]) buckets[key] = emptyTotals();
    addRow(buckets[key], row);
  });
  return Object.keys(buckets).map((period) => ({ period, ...finishTotals(buckets[period], perPeriodDays[period] || 0) }));
}

export function kindPie(totals) {
  return [
    { name: KIND_LABELS.inbound, value: totals.inbound },
    { name: KIND_LABELS.outbound, value: totals.outbound },
    { name: KIND_LABELS.automated, value: totals.automated },
  ].filter((slice) => slice.value > 0);
}

// Per-day stacked series, seeded with every day so gaps render as zero.
export function perDaySeries(rows, days) {
  const byDay = {};
  days.forEach((day) => {
    byDay[day] = {
      date: shortDay(day), day, inbound: 0, outbound: 0, automated: 0, responseTimeSum: 0, responseTimeCount: 0,
    };
  });
  rows.forEach((row) => {
    if (!byDay[row.day]) return;
    byDay[row.day][rowKind(row)] += row.count;
    byDay[row.day].responseTimeSum += row.responseTimeSum || 0;
    byDay[row.day].responseTimeCount += row.responseTimeCount || 0;
  });
  return days.map((day) => ({
    ...byDay[day],
    total: byDay[day].inbound + byDay[day].outbound + byDay[day].automated,
    avgResponseMinutes: avgResponseMinutes(byDay[day].responseTimeSum, byDay[day].responseTimeCount),
  }));
}

// Average messages per hour of day (divided by the number of days in range).
export function perHourSeries(byHourRows, totalDays) {
  const hours = Array.from({ length: 24 }, (_, h) => ({
    hour: moment(h, 'H').format('h a'), hourIndex: h, inbound: 0, outbound: 0, automated: 0,
  }));
  byHourRows.forEach((row) => {
    if (row.hour >= 0 && row.hour < 24) hours[row.hour][rowKind(row)] += row.count;
  });
  const divisor = Math.max(totalDays, 1);
  return hours.map((h) => ({
    ...h,
    inbound: Math.round((h.inbound / divisor) * 10) / 10,
    outbound: Math.round((h.outbound / divisor) * 10) / 10,
    automated: Math.round((h.automated / divisor) * 10) / 10,
    total: h.inbound + h.outbound + h.automated,
  }));
}

export function peakInboundHour(byHourRows) {
  const counts = Array(24).fill(0);
  byHourRows.forEach((row) => { if (row.incoming && row.hour >= 0 && row.hour < 24) counts[row.hour] += row.count; });
  const max = Math.max(...counts);
  if (max <= 0) return null;
  const hour = counts.indexOf(max);
  return { hour, label: `${moment(hour, 'H').format('h:mm a')} - ${moment(hour + 1, 'H').format('h:mm a')}`, count: max };
}

// Average messages by weekday (divided by how many of that weekday fall in range).
export function perWeekdaySeries(byWeekdayRows, days) {
  const occurrences = Array(7).fill(0);
  days.forEach((day) => { occurrences[moment(day, 'YYYY-MM-DD').isoWeekday() - 1] += 1; });
  const totals = WEEKDAY_LABELS.map((label) => ({ day: label, inbound: 0, outbound: 0, automated: 0 }));
  byWeekdayRows.forEach((row) => {
    if (row.weekday >= 0 && row.weekday < 7) totals[row.weekday][rowKind(row)] += row.count;
  });
  return totals.map((t, i) => {
    const divisor = Math.max(occurrences[i], 1);
    const inbound = Math.round((t.inbound / divisor) * 10) / 10;
    const outbound = Math.round((t.outbound / divisor) * 10) / 10;
    const automated = Math.round((t.automated / divisor) * 10) / 10;
    return { ...t, inbound, outbound, automated, total: inbound + outbound + automated };
  });
}

// Totals per category (member or channel), ranked by total.
export function totalsByCategory(rows, keyOf, nameOf, dayCount) {
  const buckets = {};
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key === null || key === undefined) return;
    if (!buckets[key]) buckets[key] = { key, name: nameOf(key), ...emptyTotals() };
    addRow(buckets[key], row);
  });
  return Object.values(buckets)
    .map((b) => finishTotals(b, dayCount))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

// Per-category rows per period for the table view.
export function categoryByPeriod(rows, keyOf, nameOf, days, grouping) {
  if (grouping === 'total') return totalsByCategory(rows, keyOf, nameOf, days.length);
  const perPeriodDays = daysPerPeriod(days, grouping);
  const buckets = {};
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key === null || key === undefined) return;
    const period = periodKey(row.day, grouping);
    const id = `${key}|${period}`;
    if (!buckets[id]) buckets[id] = { key, name: nameOf(key), period, ...emptyTotals() };
    addRow(buckets[id], row);
  });
  return Object.values(buckets)
    .map((b) => finishTotals(b, perPeriodDays[b.period] || 0))
    .sort((a, b) => a.name.localeCompare(b.name) || a.period.localeCompare(b.period));
}

// One line per category across the days in range: { data: [{date, [key]: n}], keys }.
export function dailyByCategory(rows, keyOf, days, maxSeries = 10) {
  const totals = {};
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key === null || key === undefined) return;
    totals[key] = (totals[key] || 0) + row.count;
  });
  const keys = Object.keys(totals).sort((a, b) => totals[b] - totals[a]).slice(0, maxSeries);
  const byDay = {};
  days.forEach((day) => {
    byDay[day] = { date: shortDay(day), day };
    keys.forEach((k) => { byDay[day][k] = 0; });
  });
  rows.forEach((row) => {
    const key = String(keyOf(row));
    if (byDay[row.day] && keys.includes(key)) byDay[row.day][key] += row.count;
  });
  return { data: days.map((day) => byDay[day]), keys };
}

export function sortRows(rows, column, ascending) {
  const sorted = [...rows].sort((a, b) => {
    const av = a[column];
    const bv = b[column];
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return String(av).localeCompare(String(bv));
  });
  return ascending ? sorted : sorted.reverse();
}
